<template>
    <div class="cart-line-return-reasons-tab modl-tab-body">
        <div class="modl-body">
            <PageLoadingSpinner v-if="loading" />
            <div v-else-if="hasPermission"
                 class="form-v">
                <!-- Return reason -->
                <div class="form-group return-reasons">
                    <label class="control-label">
                        <i class="fa-solid fa-question-square"></i>&nbsp;{{ l('ITEM_EDIT_RETURN_REASONS.REASON_LABEL') }}
                    </label>
                    <div v-if="returnReasons.length > 0"
                         class="btn-grid selectable-btns">
                        <div v-for="reason in returnReasons"
                             :key="reason.ID"
                             class="btn btn-lg btn-info btn-flex"
                             :class="{active: selectedReason?.ID === reason.ID}"
                             @click.prevent="selectReason(reason)">
                            {{ reason.description }}
                        </div>
                    </div>
                    <div v-else
                         class="alert alert-danger">
                        {{ l('ITEM_EDIT_RETURN_REASONS.NO_REASONS') }}
                    </div>
                </div>

                <!-- Return to stock -->
                <div v-if="showReturnToStockButtons"
                     class="form-group return-to-stock">
                    <label class="control-label">
                        <i class="fa-solid"
                           :class="[returnToStock ? 'fa-box-check' : 'fa-trash']"></i>&nbsp;{{ l('ITEM_EDIT_RETURN_REASONS.RETURN_LABEL') }}
                    </label>
                    <Toggle v-model="returnToStock"
                            class="toggle--non-bold"
                            label-position="after"
                            name="return-to-stock-toggle">
                        <transition :name="returnToStock ? 'slide-left' : 'slide-right'">
                            <span v-if="returnToStock">{{ l('ITEM_EDIT_RETURN_REASONS.RETURN_TO_STOCK_LABEL') }}</span>
                            <span v-else>{{ l('ITEM_EDIT_RETURN_REASONS.DO_NOT_RETURN_TO_STOCK_LABEL') }}</span>
                        </transition>
                    </Toggle>
                </div>
            </div>
            <NoResults v-else
                       icon="fa-solid fa-shield-slash"
                       lang="NO_PERMISSION"></NoResults>
        </div>

        <div class="modl-footer">
            <!-- Cancel button -->
            <SquareBtn size="lg"
                       class="cancel-btn"
                       @click="$emit('cancel')">
                {{ l(cancelLabel) }}
            </SquareBtn>

            <!-- OK button -->
            <SquareBtn :loading="saving"
                       class="ok-btn"
                       color="primary"
                       size="lg"
                       :disabled="!okEnabled"
                       @click="submit">
                {{ l('SAVE') }}
            </SquareBtn>
        </div>
    </div>
</template>

<script>
import ItemTypeEnum from '@/lib/Items/ItemType';
import NoResults from '@/vue/components/NoResults';
import PageLoadingSpinner from '@/vue/components/PageLoadingSpinner';
import PrivilegeNames from '@/lib/Privileges/PrivilegeNames';
import SquareBtn from '@/vue/components/SquareBtn';
import Toggle from '@/vue/components/Forms/Toggle';
import { CartLineDetailsTaxMixin } from '@/epos/vue/modals/CartLineDetails/CartLineDetailsTaxMixin';
import { ConfirmTabAwayMixin } from '@/vue/mixins/ConfirmTabAwayMixin';
import { container } from '@/container';
import { sortByDescription } from '@/functions/arraySorting';

export default {
    name: 'CartLineReturnsTab',

    components: {
        SquareBtn,
        Toggle,
        NoResults,
        PageLoadingSpinner,
    },

    mixins: [
        CartLineDetailsTaxMixin,
        ConfirmTabAwayMixin,
    ],

    emits: [
        'cancel',
        'resolve',
    ],

    data() {
        return {
            hasPermission: null,

            loading: true,

            saving: false,

            /**
             * @type {ReturnReason[]}
             */
            returnReasons: null,

            /**
             * @type {ReturnReason}
             */
            selectedReason: null,

            /**
             * @type {boolean|null}
             */
            returnToStock: null,

            initialReason: null,
            initialReturnToStock: null,
        };
    },

    computed: {
        okEnabled() {
            return this.selectedReason
                && (!this.showReturnToStockButtons || this.returnToStock !== null);
        },

        showReturnToStockButtons() {
            return this.cartLine.item.itemType === ItemTypeEnum.PRODUCT;
        },
    },

    async created() {
        // Check return permission.
        try {
            const privilegeCheckResult = await container.privilegesManager.requirePrivilege(
                PrivilegeNames.ISSUE_REFUNDS,
            );
            this.hasPermission = privilegeCheckResult.granted; // Will always be true.
        } catch (e) {
            // No permission.
            this.hasPermission = false;
            this.loading = false;
            return;
        }

        // Get the return reasons from the Return Reason Manager.
        const returnReasons = await container.returnReasonRepository.findAll();
        sortByDescription(returnReasons);
        this.returnReasons = returnReasons;

        // Select the appropriate reason by default.
        if (this.cartLine.returnReason.reasonID) {
            const existingReason = this.returnReasons.find((r) => r.ID === this.cartLine.returnReason.reasonID);
            if (existingReason) {
                this.selectReason(existingReason);
            }
        }
        if (this.cartLine.returnReason.returnToStock !== null) {
            this.returnToStock = !!this.cartLine.returnReason.returnToStock;
        } else if (this.showReturnToStockButtons) {
            // Default to true.
            this.returnToStock = true;
        }
        // Otherwise leave it as null.

        this.initialReason = this.selectedReason;
        this.initialReturnToStock = this.returnToStock;

        this.loading = false;
    },

    methods: {
        /**
         * @param {ReturnReason} reason
         */
        selectReason(reason) {
            this.selectedReason = reason;
        },

        submit() {
            // Set the return reason on the cart line.
            this.cartLine.returnReason.reasonID = this.selectedReason.ID;
            this.cartLine.returnReason.description = this.selectedReason.description;
            this.cartLine.returnReason.returnToStock = this.returnToStock;

            this.$emit('resolve');
        },

        hasUnsavedChanges() {
            return this.selectedReason !== this.initialReason
                || this.returnToStock !== this.initialReturnToStock;
        },
    },
};
</script>

<style lang="less">
.return-to-stock .toggle label span {
    // Add the position to make the slide transition work.
    position: absolute;
}
</style>
